import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import DrawerLayoutWrapper from 'common/drawer-layout-wrapper/DrawerLayoutWrapper';
import { useUserRolePermissions } from 'hooks';

const drawerStyles = {
  backgroundColor: 'surface.light',
  border: 0,
  height: `calc(100% - 56)`,
  top: 56,
  width: '100%',
};

const toolbarStyles = {
  backgroundColor: 'transparent',
  color: 'secondary.main',
  display: 'flex',
  minHeight: 56,
  width: '100%',
  zIndex: 4,
};

const buttonStyles = {
  '&:hover': {
    backgroundColor: 'secondary.main',
  },
  backgroundColor: 'secondary.main',
  color: 'surface.white',
  display: 'flex',
  height: 56,
  justifyContent: 'center',
  width: '100%',
};

const secondaryButtonStyles = {
  ...buttonStyles,
  '&:hover': {
    backgroundColor: 'surface.white',
  },
  backgroundColor: 'surface.white',
  color: 'secondary.main',
  width: '100%',
};

const backButtonStyles = {
  color: 'secondary.main',
  justifyContent: 'flex-start',
  m: 2,
  textTransform: 'capitalize',
};

const cardsStyles = {
  display: 'flex',
  flexFlow: 'column',
  gap: 3,
  height: '100%',
  margin: '0 auto',
  overflow: 'auto',
  pb: 10,
  px: 1,
  width: '100%',
};

const MobileDrawer = ({
  backLabel,
  filtersPanel,
  children,
  createLabel,
  module,
  onCreate,
  open,
  setTitle,
}) => {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { isDispatchingManage } = useUserRolePermissions();
  const { viewBy } = useParams();
  const navigate = useNavigate();

  const openFilters = () => {
    setFiltersOpen(true);
  };
  const closeFilters = () => {
    setFiltersOpen(false);
  };

  const handleBack = () => {
    setTitle('');
    navigate(`../${module}/${viewBy}`);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      slotProps={{
        paper: { sx: drawerStyles },
      }}
      variant="persistent"
    >
      <>
        <Toolbar disableGutters sx={toolbarStyles}>
          {isDispatchingManage && onCreate && (
            <>
              <MenuItem onClick={onCreate} sx={buttonStyles}>
                <AddRoundedIcon />
                {createLabel}
              </MenuItem>
              <Divider orientation="vertical" />
            </>
          )}
          <MenuItem
            disabled={false}
            onClick={openFilters} // TODO: set to isLoading
            sx={secondaryButtonStyles}
          >
            Filters <ChevronRightRoundedIcon />
          </MenuItem>
        </Toolbar>

        <Button onClick={handleBack} sx={backButtonStyles} variant="text">
          <ChevronLeftRoundedIcon />
          {backLabel}
        </Button>

        <Box sx={cardsStyles}>{children}</Box>
        {createPortal(
          <Drawer
            anchor="right"
            onClose={closeFilters}
            open={filtersOpen}
            slotProps={{
              backdrop: { invisible: true },
            }}
          >
            <DrawerLayoutWrapper onHeaderButtonClick={closeFilters} title="Filter">
              <Divider />
              <Stack spacing={2} sx={{ mt: 3, px: 2, width: 290 }}>
                {filtersPanel}
              </Stack>
            </DrawerLayoutWrapper>
          </Drawer>,
          document.body
        )}
      </>
    </Drawer>
  );
};

MobileDrawer.propTypes = {
  backLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  createLabel: PropTypes.string.isRequired,
  filtersPanel: PropTypes.element.isRequired,
  module: PropTypes.string.isRequired,
  onCreate: PropTypes.func,
  open: PropTypes.bool.isRequired,
  setTitle: PropTypes.func.isRequired,
};
MobileDrawer.defaultProps = {
  onCreate: undefined,
};

export default MobileDrawer;
